import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { fugueLabProjects } from '../data/portfolio'
import { PortfolioCard } from '../components/PortfolioCard'
import { ContactForm } from '../components/ContactForm'
import { useSEO } from '@/hooks/useSEO'
import { useLuxeReveal, useLuxeGrid, useLuxeHero } from '@gjl/motion/css'
import { useLuxeTextReveal } from '@gjl/motion/css'
import { trackCalendlyClick } from '@/lib/analytics'
import { Palette, TicketCheck, TrendingUp, Check, ArrowRight } from 'lucide-react'

export default function HomePage() {
  useSEO({
    title: 'Great Music Deserves to Be Heard',
    description:
      'The complete digital platform for music organizations: website, email, giving, ticketing, and music planning. Run it yourself or have us run it for you.',
    url: '/',
    canonical: '/',
  })

  // Featured projects (first 6)
  const featuredProjects = useMemo(() => {
    return fugueLabProjects.slice(0, 6)
  }, [])

  // Luxe animation refs - slower, more elegant with blur + scale
  const heroContentRef = useLuxeHero<HTMLDivElement>(0.4)
  const heroHeadlineRef = useLuxeTextReveal<HTMLHeadingElement>({ type: 'words', delay: 0.6 })
  const servicesRef = useLuxeGrid<HTMLDivElement>('.service-card', { stagger: 0.15 })
  const stepsRef = useLuxeGrid<HTMLDivElement>('.step-card', { stagger: 0.18 })
  const statsRef = useLuxeReveal<HTMLDivElement>('.stat-item', { stagger: 0.12 })
  const portfolioRef = useLuxeGrid<HTMLDivElement>('.portfolio-item', { stagger: 0.12 })
  const benefitsRef = useLuxeReveal<HTMLDivElement>('.benefit-item', { stagger: 0.1 })
  const testimonialsRef = useLuxeGrid<HTMLDivElement>('.testimonial-card', { stagger: 0.18 })

  return (
    <div className="">
      {/* Hero Section */}
      <section className="relative flex min-h-[90vh] items-center justify-center overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
          <source src="/home-hero-video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <div className="absolute inset-0 bg-black/80" />
        <div
          ref={heroContentRef}
          className="relative max-w-sm space-y-6 px-4 text-center text-white md:max-w-4xl lg:max-w-4xl"
        >
          <h1 ref={heroHeadlineRef} className="text-fluid-h1">
            Great music deserves to be heard.
          </h1>
          <p className="text-fluid-body leading-relaxed text-white/90">
            Your audience is out there. We help you reach them, with a website that works as hard as you do.
          </p>
          <p className="text-fluid-body font-medium text-white/95">
            Fugue Lab is the complete digital platform for music organizations: website, email, giving, ticketing, and
            music planning. Run it yourself, or have us run it for you.
          </p>
          <div className="flex flex-col items-center gap-4">
            <a href="/book" target="_blank" rel="noopener noreferrer" onClick={() => trackCalendlyClick('/')}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call →
                </Button>
              </motion.div>
            </a>
            <p className="mt-2 max-w-md text-sm text-white/70">
              Built by a working musician & software engineer. 15 minutes, no obligation.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-24 px-12 py-16">
        <section className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-fluid-h2">What We Do</h2>

            <div ref={servicesRef} className="mt-8 grid gap-6 md:grid-cols-3">
              {[
                {
                  icon: Palette,
                  title: 'Modern Websites',
                  link: '/services/websites',
                  description:
                    'Fast, beautiful, mobile-friendly sites with a CMS so your team can update concerts and programs without code.',
                },
                {
                  icon: TicketCheck,
                  title: 'Ticketing & Applications',
                  link: '/services/digital-systems',
                  description:
                    'Streamlined registration, tickets, and donations—with payment processing and analytics built in.',
                },
                {
                  icon: TrendingUp,
                  title: 'Ongoing Support',
                  link: '/services/digital-partnership',
                  description:
                    'Website updates, email campaigns, and strategy—an extension of your team who understands the music.',
                },
              ].map((item, idx) => {
                const Icon = item.icon
                return (
                  <Link
                    key={idx}
                    to={item.link}
                    className="service-card group border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 relative cursor-pointer space-y-4 rounded-xl border p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
                  >
                    <motion.div
                      className="bg-primary/10 text-primary inline-flex h-12 w-12 items-center justify-center rounded-lg"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <Icon className="h-6 w-6" />
                    </motion.div>
                    <h3 className="text-fluid-h4 flex items-center gap-2 text-white">{item.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                    <span className="text-primary inline-flex items-center gap-1 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1">
                      Learn more <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                )
              })}
            </div>
            <div className="mt-8 flex justify-center">
              <Link to="/packages">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button variant="default" size="lg" className="group cursor-pointer px-8">
                    View Packages & Pricing
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="space-y-8">
          <h2 className="text-fluid-h2">How It Works</h2>
          <div ref={stepsRef} className="grid gap-8 md:grid-cols-3">
            {[
              {
                num: '1',
                title: 'Free Strategy Call',
                description:
                  "Schedule a 15-minute call. We'll review your current setup and identify the biggest opportunities.",
              },
              {
                num: '2',
                title: 'Custom Strategy',
                description:
                  "We create a tailored plan—whether that's a new website, streamlined systems, or ongoing digital partnership.",
              },
              {
                num: '3',
                title: 'Launch & Grow',
                description:
                  'We build, you focus on the music. Then we help you grow your audience with ongoing support.',
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="step-card border-border/50 from-primary/5 hover:border-primary/30 relative space-y-4 rounded-xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              >
                <motion.div
                  className="bg-primary text-primary-foreground inline-flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {step.num}
                </motion.div>
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Classical Music Organizations Social Proof Section */}
        <section className="space-y-8 text-center">
          <div className="space-y-4">
            <h2 className="text-fluid-h2">Built by Musicians, For Musicians</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              We specialize in digital systems for the classical music community—from festivals and chamber ensembles to
              opera companies, choral organizations, and sacred music programs.
            </p>
          </div>

          <div
            ref={statsRef}
            className="from-primary/10 via-primary/5 border-primary/20 mx-auto max-w-4xl rounded-2xl border bg-gradient-to-br to-transparent p-8 shadow-lg md:p-12"
          >
            <div className="grid gap-8 text-center md:grid-cols-3">
              <motion.div
                className="stat-item hover:bg-primary/5 space-y-3 rounded-xl p-4 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="text-primary text-4xl font-bold md:text-5xl">10+</div>
                <div className="text-muted-foreground text-sm font-medium">Music organizations launched</div>
              </motion.div>
              <motion.div
                className="stat-item hover:bg-primary/5 space-y-3 rounded-xl p-4 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="text-primary text-4xl font-bold md:text-5xl">5+</div>
                <div className="text-muted-foreground text-sm font-medium">Hours saved per week on average</div>
              </motion.div>
              <motion.div
                className="stat-item hover:bg-primary/5 space-y-3 rounded-xl p-4 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="flex justify-center">
                  <motion.div
                    className="bg-primary/20 inline-flex h-12 w-12 items-center justify-center rounded-full"
                    whileHover={{ scale: 1.1, rotate: 10 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                  >
                    <Check className="text-primary h-8 w-8" strokeWidth={3} />
                  </motion.div>
                </div>
                <div className="text-muted-foreground text-sm font-medium">Built by musicians for musicians</div>
              </motion.div>
            </div>
          </div>

          <div className="pt-4">
            <p className="text-muted-foreground italic">
              Featured Client:{' '}
              <Link to="/portfolio/chroma-festival" className="text-primary font-semibold underline">
                Chroma International Music Festival
              </Link>
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-fluid-h2">Featured Work</h2>
          </div>
          <div ref={portfolioRef} className="max-w-6xl columns-1 gap-8 space-y-8 md:columns-2">
            {featuredProjects.map((project) => (
              <div key={project.slug} className="portfolio-item">
                <PortfolioCard {...project} />
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <Link to="/portfolio">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button className="cursor-pointer">See All Projects</Button>
              </motion.div>
            </Link>
          </div>
        </section>

        {/* What You Get */}
        <section
          ref={benefitsRef}
          className="from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-gradient-to-br to-transparent p-8 md:p-12"
        >
          <h2 className="text-fluid-h3">What You Get</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {[
              "A website you're proud to share—one that actually fills seats.",
              'Ticketing and registration that runs itself. No more spreadsheet chaos.',
              'More time for rehearsals and performances, less time wrestling with technology.',
              'A growing email list of audience members who actually show up.',
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className="benefit-item flex items-start gap-3"
                whileHover={{ x: 4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="mt-1 flex-shrink-0">
                  <Check className="text-primary h-5 w-5" strokeWidth={2.5} />
                </div>
                <p className="text-muted-foreground leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl space-y-8">
          <h2 className="text-fluid-h2">What Clients Say</h2>
          <div ref={testimonialsRef} className="grid gap-6 md:grid-cols-2">
            <motion.blockquote
              className="testimonial-card border-border/50 from-primary/5 hover:border-primary/30 relative rounded-xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary/20 absolute -top-3 -left-1 font-serif text-6xl">"</div>
              <p className="text-muted-foreground relative z-10 leading-relaxed italic">
                My experience working with Garrett was a pleasure from start to finish. He was organized, efficient and
                highly professional in helping to realize my vision for my website... Can&apos;t recommend him highly
                enough.
              </p>
              <footer className="border-border/50 mt-4 border-t pt-4">
                <div className="text-foreground font-semibold">Matt Mueller</div>
                <div className="text-muted-foreground text-sm">Baritone</div>
              </footer>
            </motion.blockquote>
            <motion.blockquote
              className="testimonial-card border-border/50 from-primary/5 hover:border-primary/30 relative rounded-xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary/20 absolute -top-3 -left-1 font-serif text-6xl">"</div>
              <p className="text-muted-foreground relative z-10 leading-relaxed italic">
                I highly recommend enlisting Garrett's expertise to design (or redesign) your website... he creates a
                product even better than you could have imagined on your own.
              </p>
              <footer className="border-border/50 mt-4 border-t pt-4">
                <div className="text-foreground font-semibold">Dr. Susan Bengtson</div>
                <div className="text-muted-foreground text-sm">Violist & Educator</div>
              </footer>
            </motion.blockquote>
          </div>
        </section>

        <ContactForm
          className="pt-12"
          header="Ready to Focus on the Music?"
          body="Let's talk about modernizing your online presence—so you can fill more seats, grow your audience, and spend less time on technology."
          showBooking
          bookingHeading="Book a Free Call"
        />
      </div>
    </div>
  )
}
